// Every display string for the Getting Started checklist lives here.
//
// The API sends stable ids (`storefront-setup`, `gettingStarted`, ...) and never sends copy, so a
// wording change is a one-line edit in this file with no component, model, or API change. Nothing
// else in the feature should hardcode user-visible text.
//
// Unknown ids are expected: the API can ship a new step before the web build knows about it. Use
// getStepStrings / getCategoryStrings, which fall back to a readable title derived from the id
// instead of rendering a blank row.

import { GettingStartedStepStatus } from '../../models/GettingStarted';

/** Copy shown around the checklist itself, independent of any one step. */
export const CHECKLIST_STRINGS = {
    panelTitle: 'Getting Started',
    panelSubtitle: 'A few quick steps to get the most out of Adaptive Catalog.',
    buttonAriaLabel: 'Getting Started checklist',
    /** Overall progress line, e.g. "1 of 2 complete". */
    progressLabel: (complete: number, total: number) => `${complete} of ${total} complete`,
    allCompleteTitle: 'You are all set',
    allCompleteDescription: 'Every getting started step is done. Come back any time to review them.',
    emptyMessage: 'There is nothing to set up right now.',
    loading: 'Loading your checklist...',
};

/** Copy driven purely by status, shared by every step. */
export const STATUS_STRINGS: Record<GettingStartedStepStatus, string> = {
    locked: 'Finish the earlier steps first',
    available: 'Ready to start',
    inProgress: 'In progress',
    processing: 'Working on it',
    needsReview: 'Ready for your review',
    complete: 'Complete',
};

export interface ICategoryStrings {
    title: string;
    description?: string;
}

/**
 * Section headings. The key order here is the render order; any category the API sends that is not
 * listed renders after these, in the order the API returned it.
 */
export const CATEGORY_STRINGS: Record<string, ICategoryStrings> = {
    gettingStarted: {
        title: 'Getting Started',
        description: 'The essentials. Do these first.',
    },
    moreToExplore: {
        title: 'More to Explore',
        description: 'Optional next steps once the basics are in place.',
    },
};

/** Render order for known categories. */
export const CATEGORY_ORDER: string[] = Object.keys(CATEGORY_STRINGS);

/**
 * Copy for the guided CRM setup dialog the `crm-integration` step opens. It lives here with the rest
 * of the onboarding wording so a rewrite stays a one-file edit.
 */
export const CRM_SETUP_STRINGS = {
    title: 'Connect your CRM',
    /** The dialog runs two halves that look alike; naming them apart is what tells you which one
     * you are in, and therefore whether suggestions belong on this screen at all. */
    configureTitle: 'Configure your integration',
    intro: 'Pick your CRM and give us credentials. We will take care of the rest of the setup for you.',
    loadingCandidates: 'Looking for available CRM integrations...',
    empty: 'There are no CRM integrations available for this organization yet. Talk to your account team to have one enabled.',
    loadFailed: 'We could not load the list of CRM integrations. Please try again.',
    alreadyConnected: 'Connected',
    connectedTooltip: 'This CRM already has working credentials. Open it to review or update them.',
    preparing: 'Setting things up...',
    setupFailed: 'We could not set that CRM up. Please try again.',
    loadingFields: 'Loading credential fields...',
    credentialsIntro: (name: string) => `Enter the credentials Adaptive Catalog should use to reach ${name}.`,
    /** The subtle line naming what quick setup turned on. */
    willEnable: (names: string[]) => `We will set up: ${names.join(', ')}.`,
    helpHeading: 'How to generate these credentials',
    missingFields: 'Fill in every required field to continue.',
    validateLabel: 'Validate and continue',
    validatingLabel: 'Validating...',
    validateFailed: 'We could not reach your CRM with those credentials.',
    moreInfo: 'More info',
    success: 'Your CRM is connected. Imports will start on their schedule.',
    /** Follows `success`, as a link rather than a direction to go and find the place yourself. */
    successRunImport: 'Open its settings to run one now',
    back: 'Choose a different CRM',
    close: 'Close',
    cancel: 'Cancel',
    done: 'Done',

    // Credentials step, connectors without a validation endpoint.
    cannotValidate: 'This CRM does not offer a way to check credentials automatically. Save them and confirm the connection with your first import.',
    saveContinueLabel: 'Save and continue',

    // Capabilities step.
    capabilitiesIntro: (name: string) => `Choose what your ${name} connection should do. You can change any of this later.`,
    capabilityImport: 'Import companies and contacts',
    capabilityImportDescription: 'Bring your companies, contacts, and purchase history into Adaptive Catalog on a schedule.',
    capabilityOpportunity: 'Create opportunities from storefront orders',
    capabilityOpportunityDescription: 'When a customer checks out on your storefront, create an opportunity in your CRM.',
    capabilityCustomerLookup: 'Look up customers at storefront signup',
    capabilityCustomerLookupDescription: 'Match new storefront shoppers against your existing customer records.',
    advancedShow: 'Advanced options',
    advancedHide: 'Hide advanced options',
    advancedCapabilitiesHeading: 'Individual connection features',
    advancedSlotsHeading: 'Storefront integration slots',
    slotOpportunity: 'Opportunity creation',
    slotCustomerLookup: 'Customer lookup',
    slotLocationLookup: 'Location lookup',
    slotLeave: 'Leave as it is',
    slotUse: (name: string) => `Use ${name}`,
    slotDisabled: 'None (Disabled)',
    slotAssignedElsewhere: (label: string) =>
        `${label} is already handled by another integration. Use the advanced options if you want this connection to take it over.`,
    slotOverwriteWarning: 'One or more storefront integrations currently point at another integration and will be switched to this connection.',
    savingCapabilities: 'Saving your choices...',
    capabilitiesSaveFailed: 'We could not save those choices. Please try again.',
    capabilitiesNext: 'Continue',
    quoteLinkOffer: 'Add the quote link to ConnectWise PSA for me',
    quoteLinkOfferDetail: 'Opportunities get a quote button that builds the workspace and prepared cart in Adaptive Catalog automatically.',
    quoteLinkCreated: 'Quote link added to ConnectWise PSA',
    quoteLinkExisted: 'ConnectWise PSA already has this quote link',
    quoteLinkFailed: 'The quote link could not be added to ConnectWise PSA — you can add it later from the folio settings.',

    // Field mapping step.
    mappingIntro: 'A few settings tell your CRM connection how to file what it creates. Required settings are shown first.',
    mappingSuggestionsChecking: 'Reading your CRM for suggested settings. You can fill things in yourself in the meantime.',
    mappingSuggestionsApplied: (count: number) =>
        `${count} suggested ${count === 1 ? 'value' : 'values'} from your CRM data ${count === 1 ? 'has' : 'have'} been filled in. Review before saving.`,
    mappingNothingRequired: 'Nothing here needs an answer right now.',
    /** Heading on the pinned band of settings still waiting on the user. */
    mappingNeedsYou: (count: number) =>
        count === 1 ? '1 setting needs your input' : `${count} settings need your input`,
    mappingNothingOutstanding: 'Every required setting has a value. Review them below and continue.',
    mappingAlreadySet: 'Already set',
    mappingShowAll: 'Show all settings',
    mappingRequiredOnly: 'Show required settings only',
    mappingSaveFailed: 'We could not save those settings. Please try again.',
    mappingNext: 'Save and continue',

    // Proposed setup review (wedge-sourced organizations).
    proposalChecking: 'Checking for a suggested setup...',
    proposalPendingDetail: 'We are preparing a suggested configuration based on what we know about your organization. This usually takes under a minute. You can skip ahead and set things up yourself.',
    proposalHeading: 'Here is a suggested setup for you to review',
    proposalIntro: 'Based on what we know about your organization, we prepared a starting point. Nothing is applied until you confirm, and you can change any of it in the following steps. Credentials are never suggested; you will enter those yourself.',
    proposalConfidence: (percent: number) => `Suggestion confidence: ${percent}%`,
    proposalCapabilitiesHeading: 'Suggested features',
    proposalFieldsHeading: 'Suggested settings',
    proposalSlotsHeading: 'Suggested storefront wiring',
    proposalApply: 'Use selected suggestions',
    proposalSkip: 'Set up without suggestions',
    proposalApplyFailed: 'We could not record your choices, but the selected values were applied to the wizard.',
    proposalSlotLabel: (slot: string, name: string) => `${slot}: ${name}`,

    // End of the connecting half, when configuring is a separate checklist step.
    connectedHeading: 'Your CRM is connected',
    connectedBody: 'We are already reading your CRM to suggest settings for the next step. Head back to Getting Started and pick "Configure your integration" when you are ready; the suggestions will be waiting.',
    connectedDone: 'Back to Getting Started',

    // Summary step.
    summaryHeading: (name: string) => `${name} is connected. Here is what was set up:`,
    summaryCapabilityOn: (name: string) => `${name}: on`,
    summarySlotWired: (label: string) => `${label}: handled by this connection`,
    summaryVerified: 'Credentials verified',
    summaryNotVerified: 'Credentials saved. This CRM cannot be verified automatically.',
    summaryChecklistNote: 'Imports run on their schedule. You can revisit any of this from the Extensions page.',

    // Test opportunity.
    testOpportunityIntro: 'Not sure these settings are right? We can create one opportunity in your CRM using stand-in order details, so you can see exactly what a real order will produce.',
    testOpportunityAction: 'Create a test opportunity',
    testOpportunityRunning: 'Creating it in your CRM...',
    testOpportunitySuccess: (reference?: string) => reference
        ? `Created opportunity ${reference} in your CRM. It is a real record named for the test, so delete it when you are done looking.`
        : 'Created a test opportunity in your CRM. It is a real record named for the test, so delete it when you are done looking.',
    testOpportunityOpen: 'Open it in your CRM',
    testOpportunityFailed: 'The CRM would not accept these settings',
};

/**
 * Copy for the individual connections a multi-part step lists under itself. Keyed by the item's own
 * status, which is always one of these three.
 */
export const ITEM_STATUS_STRINGS: Record<'inProgress' | 'needsReview' | 'complete', string> = {
    inProgress: 'Waiting to be verified',
    needsReview: 'Needs attention',
    complete: 'Verified and working',
};

/** Label on the link that adds one more of whatever a multi-part step tracks. */
export const ADD_ANOTHER_LABEL = 'Connect another';

/**
 * Copy for the guided "connect a distributor" flow. Status wording matches the checklist's, because the
 * two describe the same connections and two vocabularies for one fact is how they drift apart.
 */
export const DISTRIBUTOR_STRINGS = {
    // Step 1: choose.
    title: 'Connect a distributor',
    intro: 'Pick a distributor to get started. We will walk you through what it can do and what you need.',
    loading: 'Looking for available distributors...',
    loadFailed: 'We could not load the list of distributors. Please try again.',
    empty: 'There are no distributors available for this organization yet. Talk to your account team to have one enabled.',
    connect: 'Set up',
    openConnection: 'Review',
    /** One line summarising what a distributor offers, without listing every capability. */
    offers: (kinds: string[]) => kinds.join(' · '),
    kindImport: 'Catalog',
    kindRealtime: 'Live pricing',
    kindProcurement: 'Ordering',

    // Step 2: what to set up.
    capabilitiesTitle: (name: string) => `What should ${name} do?`,
    capabilitiesIntro: 'Turn on what you need now. You can add the rest later without starting over.',
    capabilitiesNone: 'Choose at least one to continue.',
    capabilitiesNext: 'Continue',

    // Step 3: readiness.
    readinessTitle: (name: string) => `Do you have your ${name} credentials?`,
    readinessIntro: 'Each part you picked needs its own credentials, and they come from different places.',
    readinessYes: 'Yes, I have them',
    readinessNo: 'Not yet',
    readinessHelpTitle: 'Where to get them',
    /** Shown per selected capability on the "not yet" path. */
    readinessHelpFor: (name: string) => `For ${name}:`,
    readinessNoHelp: 'We do not have a link for this one yet. Your account manager at the distributor can set it up.',
    readinessComeBack: 'I will come back to this',
    readinessHaveThemNow: 'I have them now',
    readinessSaved: 'We saved your progress. This distributor will show as unfinished until its credentials are verified.',

    // Step 4: credentials.
    credentialsTitle: (name: string) => `Connect to ${name}`,
    credentialsShared: 'Account credentials',
    credentialsSharedIntro: 'Used by everything you turned on.',
    credentialsFor: (name: string) => `${name} credentials`,
    loadingFields: 'Loading credential fields...',
    cannotValidate: 'This distributor does not offer a way to check credentials automatically. Save them and confirm with the first run.',
    credentialProgress: (current: number, total: number) => `Step ${current} of ${total}`,
    verifyAndContinue: 'Verify and continue',
    capabilityVerified: (name: string) => `${name} verified. Now for the next set of credentials.`,
    loadFieldsFailed: 'We could not load the credential fields for this distributor. Please try again.',
    doneUnverifiedCapability: 'on, not yet verified',
    validateLabel: 'Connect and verify',
    saveLabel: 'Save credentials',
    validating: 'Checking your credentials...',
    setupFailed: 'We could not start setting this distributor up. Please try again.',
    validateFailed: 'We could not reach the distributor with those credentials.',
    validateSomeFailed: 'Some of these could not be verified. Each problem is shown next to the credentials it belongs to.',
    helpHeading: 'How to get these credentials',

    // Step 5: done.
    doneTitle: (name: string) => `${name} is connected`,
    doneVerified: 'Credentials verified.',
    doneNotVerified: 'Credentials saved. This distributor cannot be verified automatically.',
    doneWhatNext: 'Imports run on their schedule. You can change any of this from the Extensions page.',
    doneAddAnother: 'Connect another distributor',

    back: 'Back',
    close: 'Close',
    done: 'Done',

    status: {
        inProgress: 'Waiting to be verified',
        needsReview: 'Needs attention',
        complete: 'Verified and working',
    } as Record<string, string>,
};

export interface IStepStrings {
    title: string;
    description: string;
    /** Label on the call to action while the step is available or in progress. */
    ctaLabel: string;
    /** Optional override for a step the user has started but not finished. */
    inProgressDescription?: string;
    /** Optional overrides for the two background-work states. */
    processingDescription?: string;
    processingCtaLabel?: string;
    reviewDescription?: string;
    reviewCtaLabel?: string;
    /** Shown once the step is complete. Falls back to `description`. */
    completeDescription?: string;
}

export const STEP_STRINGS: Record<string, IStepStrings> = {
    'storefront-setup': {
        title: 'Set up your first Storefront',
        description: 'Create a branded store your customers can buy from. It takes a few minutes and you can restyle it later.',
        ctaLabel: 'Create a Storefront',
        processingDescription: 'We are building your storefront. This runs in the background, so feel free to keep working.',
        processingCtaLabel: 'View progress',
        reviewDescription: 'Your storefront is ready. Take a look and publish it when it feels right.',
        reviewCtaLabel: 'Review your storefront',
        completeDescription: 'Your storefront is live. You can keep tuning its look and products any time.',
    },
    'crm-integration': {
        title: 'Integrate your CRM',
        description: 'Connect ConnectWise or Autotask to bring your products, contacts, and purchase history into Adaptive Catalog.',
        ctaLabel: 'Connect',
        completeDescription: 'Your CRM is connected. Imports run on their schedule from the Extensions page.',
    },
    'configure-integrations': {
        title: 'Configure your integration',
        description: 'Choose what your CRM connection should do and confirm a few settings. We prepare suggestions for you from your own CRM data.',
        ctaLabel: 'Configure',
        processingDescription: 'We are reading your CRM to suggest settings. This runs in the background, so come back when it suits you.',
        processingCtaLabel: 'Configure anyway',
        completeDescription: 'Your integration is configured. You can change any of it from the Extensions page.',
    },
    'connect-distributors': {
        title: 'Connect your distributors',
        description: 'Add your distributor accounts to pull live pricing, availability, and ordering into your catalog. Connect as many as you buy from.',
        ctaLabel: 'Connect a distributor',
        inProgressDescription: 'We check each connection against the distributor before it counts as working. Anything still waiting is listed below.',
        reviewDescription: 'One of your distributor connections needs attention before it can be used.',
        reviewCtaLabel: 'Review your distributors',
        completeDescription: 'Your distributors are connected and verified. Add another any time.',
    },
    'favorite-products': {
        title: 'Find some Favorite Products',
        description: 'Search the catalog and star a few products you commonly sell. Favorites make quoting and storefront curation much faster.',
        ctaLabel: 'Search products',
        completeDescription: 'You have starred enough products to get going. Keep adding favorites as you go.',
    },
};

/** Turns an unknown id such as `some-new-step` into `Some New Step` so nothing renders blank. */
const titleFromId = (id: string): string =>
    id
        .split(/[-_\s]+/)
        .filter(Boolean)
        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');

export const getCategoryStrings = (categoryId: string): ICategoryStrings =>
    CATEGORY_STRINGS[categoryId] ?? { title: titleFromId(categoryId) };

export const getStepStrings = (stepId: string): IStepStrings =>
    STEP_STRINGS[stepId] ?? {
        title: titleFromId(stepId),
        description: '',
        ctaLabel: 'Open',
    };

/** The description to show for a step, taking its status-specific override into account. */
export const getStepDescription = (stepId: string, status: GettingStartedStepStatus): string => {
    const strings = getStepStrings(stepId);
    switch (status) {
        case 'inProgress':
            return strings.inProgressDescription ?? strings.description;
        case 'processing':
            return strings.processingDescription ?? strings.description;
        case 'needsReview':
            return strings.reviewDescription ?? strings.description;
        case 'complete':
            return strings.completeDescription ?? strings.description;
        default:
            return strings.description;
    }
};

/** The CTA label to show for a step, taking its status-specific override into account. */
export const getStepCtaLabel = (stepId: string, status: GettingStartedStepStatus): string => {
    const strings = getStepStrings(stepId);
    switch (status) {
        case 'processing':
            return strings.processingCtaLabel ?? strings.ctaLabel;
        case 'needsReview':
            return strings.reviewCtaLabel ?? strings.ctaLabel;
        default:
            return strings.ctaLabel;
    }
};
